// GovtFeeHistory.jsx

import React, { useState, useMemo, useEffect } from 'react';
import './GovtFeeStyles.css';
import {
  GovtPageInfo,
  GovtFormatDate,
  GovtFormatCurrency,
  GovtCalculatePercentage,
  GovtItemsPerPage,
  matchesCountrySearch,
  GovtSearchConfig,
  GovtEmptyState,
  GovtTableHeaders,
  GovtPaginationConfig,
  GovtNoticeInfo,

  GovtFormatShowingRecords,
} from './GovtFeeData';
import configService from "../../../../services/configService";

// Convert snake_case keys to camelCase
function toCamel(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}
function mapKeys(obj) {
  if (Array.isArray(obj)) return obj.map(mapKeys);
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [toCamel(k), mapKeys(v)])
    );
  }
  return obj;
}

const GovtFeeHistoryPage = () => {
  const [feeHistory, setFeeHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [visaTypeFilter, setVisaTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const todayStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  useEffect(() => {
    configService.getConfig().then(r => {
      const raw = Array.isArray(r?.govtFeeHistory) ? r.govtFeeHistory :
                  Array.isArray(r?.data?.govtFeeHistory) ? r.data.govtFeeHistory : [];
      setFeeHistory(raw.map(mapKeys));
      setDataLoaded(true);
    }).catch(() => { setDataLoaded(true); });
  }, []);

  const statistics = useMemo(() => {
    const totalChanges = feeHistory.length;
    const increases = feeHistory.filter(item => item.changeType === 'increase').length;
    const decreases = feeHistory.filter(item => item.changeType === 'decrease').length;
    const countriesAffected = new Set(feeHistory.map(item => item.countryCode)).size;
    return { totalChanges, increases, decreases, countriesAffected };
  }, [feeHistory]);
  const uniqueVisaTypes = useMemo(
    () => [...new Set(feeHistory.map(item => item.visaType).filter(Boolean))].sort(),
    [feeHistory]
  );
  const availableCountries = useMemo(
    () =>
      [...new Set(feeHistory.map(item => JSON.stringify({ code: item.countryCode, name: item.countryName })))]
        .map((raw) => JSON.parse(raw))
        .filter((country) => country.code && country.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [feeHistory]
  );

  const filteredData = useMemo(() => {
    return feeHistory.filter(item => {
      const matchesSearch =
        matchesCountrySearch(item.countryName, searchTerm) ||
        (item.visaType && item.visaType.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.reason && item.reason.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCountry = countryFilter === '' || item.countryCode === countryFilter;
      const matchesVisaType = visaTypeFilter === '' || item.visaType === visaTypeFilter;

      return matchesSearch && matchesCountry && matchesVisaType;
    });
  }, [feeHistory, searchTerm, countryFilter, visaTypeFilter]);

  const paginatedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => (a.countryName || "").localeCompare(b.countryName || ""));
    const startIndex = (currentPage - 1) * GovtItemsPerPage;
    return sorted.slice(startIndex, startIndex + GovtItemsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / GovtItemsPerPage);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setHasSearched(true);
    setCurrentPage(1);
  };

  const handleCountryChange = (e) => {
    setCountryFilter(e.target.value);
    setHasSearched(true);
    setCurrentPage(1);
  };

  const handleVisaTypeChange = (e) => {
    setVisaTypeFilter(e.target.value);
    setHasSearched(true);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = GovtPaginationConfig.maxVisiblePages;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`GovtPaginationButton ${currentPage === i ? 'GovtPaginationButtonActive' : ''}`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="GovtPagination">
        <button
          className="GovtPaginationButton"
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        {startPage > 1 && (
          <>
            <button
              className="GovtPaginationButton"
              onClick={() => setCurrentPage(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="GovtPaginationEllipsis">{GovtPaginationConfig.ellipsis}</span>}
          </>
        )}
        {pages}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="GovtPaginationEllipsis">{GovtPaginationConfig.ellipsis}</span>}
            <button
              className="GovtPaginationButton"
              onClick={() => setCurrentPage(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
        <button
          className="GovtPaginationButton"
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="GovtContainer">
      <header className="GovtHeader" data-aos="fade-down">
        <h1 className="GovtTitle">{GovtPageInfo.title}</h1>
        <p className="GovtSubtitle">{GovtPageInfo.subtitle}</p>
      </header>

      <div className="GovtSearchSection" data-aos="fade-right" data-aos-delay="80">
        <div className="GovtSearchBarWrapper">
          <svg className="GovtSearchIcon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3a6fa8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="GovtSearchInput"
            placeholder={GovtSearchConfig.placeholder}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        <div className="GovtFiltersRow">
          <div className="GovtFilterWrap">
            <div style={{position:"relative",display:"inline-block",width:"100%"}}>
              <select
                className="GovtFilterSelect"
                value={countryFilter}
                onChange={handleCountryChange}
                style={{appearance:"none",paddingRight:30}}
              >
                <option value="">{GovtSearchConfig.allCountriesOption}</option>
                {availableCountries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",color:"#64748b"}}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            {countryFilter && (
              <button
                className="GovtFilterClear"
                onClick={() => { setCountryFilter(''); setHasSearched(true); setCurrentPage(1); }}
                aria-label="Clear country filter"
                type="button"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          <div className="GovtFilterWrap">
            <div style={{position:"relative",display:"inline-block",width:"100%"}}>
              <select
                className="GovtFilterSelect"
                value={visaTypeFilter}
                onChange={handleVisaTypeChange}
                style={{appearance:"none",paddingRight:30}}
              >
                <option value="">{GovtSearchConfig.allVisaTypesOption}</option>
                {uniqueVisaTypes.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",color:"#64748b"}}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            {visaTypeFilter && (
              <button
                className="GovtFilterClear"
                onClick={() => { setVisaTypeFilter(''); setHasSearched(true); setCurrentPage(1); }}
                aria-label="Clear visa type filter"
                type="button"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {filteredData.length === 0 && hasSearched && dataLoaded ? (
        <div className="GovtNoResults">
          <div className="GovtNoResultsText">{GovtEmptyState.title}</div>
          <div className="GovtNoResultsHint">
            {GovtEmptyState.hint}
          </div>
        </div>
      ) : filteredData.length > 0 ? (
        <>
          <div className="GovtTableWrapper">
            <table className="GovtTable">
              <thead className="GovtTableHeader">
                <tr>
                  {GovtTableHeaders.map(header => (
                    <th key={header} className="GovtTableHeaderCell">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map(item => (
                  <tr key={item.id} className="GovtTableRow">
                    <td className="GovtTableCell GovtCountryCell">
                      {item.countryName}
                      {/* <span className="GovtCountryCode">{item.countryCode}</span> */}
                    </td>
                    <td className="GovtTableCell">{item.visaType}</td>
                    <td className="GovtTableCell GovtDateCell">
                      {GovtFormatDate(item.effectiveDate)}
                    </td>
                    <td className="GovtTableCell GovtFeeCell">
                      <span className="GovtFeeOld">
                        {GovtFormatCurrency(item.oldFee, item.currency)}
                      </span>
                      <span className="GovtFeeNew">
                        {GovtFormatCurrency(item.newFee, item.currency)}
                      </span>
                    </td>
                    <td className="GovtTableCell">
                      <span
                        className={`GovtChangeIndicator ${
                          item.changeType === 'increase'
                            ? 'GovtChangeIncrease'
                            : 'GovtChangeDecrease'
                        }`}
                      >
                        {item.changeType === 'increase' ? '+' : ''}
                        {GovtCalculatePercentage(item.oldFee, item.newFee)}%
                      </span>
                    </td>
                    <td className="GovtTableCell GovtReasonCell">
                      {item.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
          <div className="GovtPaginationInfo">
            {GovtFormatShowingRecords(
              ((currentPage - 1) * GovtItemsPerPage) + 1,
              Math.min(currentPage * GovtItemsPerPage, filteredData.length),
              filteredData.length
            )}
          </div>
        </>
      ) : null}

      <footer className="GovtFooter" data-aos="fade-up">
        <div className="GovtFooterTop">
                          <span className="GovtLastUpdated">Last updated: {todayStr}</span>
          <span className="GovtNoticeLabel">{GovtNoticeInfo.title}</span>
        </div>
        <p className="GovtDisclaimerText">{GovtPageInfo.disclaimer}</p>

      </footer>
    </div>
  );
};

export default GovtFeeHistoryPage;
